import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { inr } from "../lib/api";
import { useCart } from "../lib/cart";
import { useWishlist } from "../lib/wishlist";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Star,
  Plus,
  Minus,
  Check,
  Truck,
  ShieldCheck,
  RotateCcw,
  RotateCw,
  Sparkles,
  Activity,
  Move,
  MoveUp,
  Battery,
  BatteryCharging,
  Car,
  Briefcase,
  Award,
  Settings,
  Wind,
  Snowflake,
  Moon,
  Sunrise,
  Anchor,
  Spline,
  PersonStanding,
  Target,
  Heart,
  Layers,
  TrendingUp,
  Droplets,
  Feather,
  Zap,
  Share2,
  Twitter,
  Facebook,
  MessageCircle,
  Link2,
  Loader2,
} from "lucide-react";
import ProductCard from "../components/ProductCard";
import ProductGallery from "../components/ProductGallery";

const BENEFIT_ICONS = [
  { keys: ["cradle", "360"], icon: RotateCw },
  { keys: ["fatigue", "tired", "stiff", "morning", "strain"], icon: BatteryCharging },
  { keys: ["posture", "correction", "improve"], icon: MoveUp },
  { keys: ["lumbar", "curve", "spine", "spinal", "alignment", "align"], icon: Spline },
  { keys: ["tailbone", "coccyx", "hip", "pelvic", "seat"], icon: Anchor },
  { keys: ["cervical", "neck"], icon: PersonStanding },
  {
    keys: [
      "office",
      "car",
      "home",
      "travel",
      "anywhere",
      "everyday",
      "everyday-ready",
      "ready",
    ],
    icon: Briefcase,
  },
  { keys: ["sleep", "night", "rest"], icon: Moon },
  { keys: ["breath", "cool", "vent", "air"], icon: Snowflake },
  { keys: ["pressure", "relief"], icon: Wind },
  { keys: ["memory foam", "foam", "layer"], icon: Layers },
  { keys: ["soft", "comfort", "gentle"], icon: Heart },
  { keys: ["adjust", "strap", "snap"], icon: Settings },
  { keys: ["pack", "compact", "light", "portable"], icon: Feather },
  { keys: ["durable", "warranty", "long-lasting"], icon: Award },
  { keys: ["recovery", "mobility", "boost"], icon: TrendingUp },
  { keys: ["protect", "support", "shield", "guard"], icon: ShieldCheck },
];

function iconForBenefit(text) {
  const t = (text || "").toLowerCase();

  for (const row of BENEFIT_ICONS) {
    if (row.keys.some((k) => t.includes(k))) {
      return row.icon;
    }
  }

  return Sparkles;
}

export default function ProductPage() {
  const { slug } = useParams();

  const { addItem, setCartOpen } = useCart();
  const { has: wishHas, toggle: wishToggle } = useWishlist();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: "",
    body: "",
  });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const ratingBreakdown = useMemo(() => {
    const buckets = {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0,
    };

    reviews.forEach((r) => {
      if (buckets[r.rating] !== undefined) {
        buckets[r.rating] += 1;
      }
    });

    return buckets;
  }, [reviews]);

  const submitReview = async () => {
    if (!reviewForm.title.trim() || !reviewForm.body.trim()) {
      toast.error("Please write a short title and review.");
      return;
    }

    setReviewSubmitting(true);

    try {
      const { data } = await api.post("/reviews", {
        product_id: product.id,
        name: user?.name || "Anonymous",
        rating: reviewForm.rating,
        title: reviewForm.title.trim(),
        body: reviewForm.body.trim(),
      });

      setReviews((prev) => [data, ...prev]);

      setReviewForm({
        rating: 5,
        title: "",
        body: "",
      });

      setShowReviewForm(false);
      toast.success("Thanks — your review is live!");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not submit review."
      );
    } finally {
      setReviewSubmitting(false);
    }
  };

  const sharePayload = useMemo(() => {
    if (!product) return null;

    const url =
      typeof window !== "undefined" ? window.location.href : "";

    const text = `${product.name} — ${product.tagline} · ${inr(
      product.price
    )}`;

    return {
      url,
      text,
    };
  }, [product]);

  const share = async (channel) => {
    if (!sharePayload) return;

    const { url, text } = sharePayload;

    if (channel === "native") {
      if (navigator.share) {
        try {
          await navigator.share({
            title: product.name,
            text,
            url,
          });
        } catch {
          /* user cancelled */
        }
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied!");
      }

      return;
    }

    if (channel === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied!");
      } catch {
        toast.error("Could not copy");
      }

      return;
    }

    const shareLinks = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}`,

      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url
      )}`,

      whatsapp: `https://wa.me/?text=${encodeURIComponent(
        `${text} ${url}`
      )}`,
    };

    if (shareLinks[channel]) {
      window.open(
        shareLinks[channel],
        "_blank",
        "noopener,noreferrer,width=600,height=540"
      );
    }
  };

  useEffect(() => {
    (async () => {
      window.scrollTo({
        top: 0,
      });

      try {
        const { data } = await api.get(`/products/${slug}`);

        setProduct(data);

        const [r, rev] = await Promise.all([
          api.get(`/products/${slug}/related`),
          api.get(`/products/${data.id}/reviews`),
        ]);

        setRelated(r.data);
        setReviews(rev.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [slug]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-20">
        Loading...
      </div>
    );
  }

  return (
    <div
      data-testid="product-page"
      className="max-w-7xl mx-auto px-6 lg:px-10 py-10"
    >
      <nav className="text-xs text-welljio-muted mb-8">
        <Link to="/" className="hover:underline">
          Home
        </Link>{" "}
        ·{" "}
        <Link to="/shop" className="hover:underline">
          Shop
        </Link>{" "}
        · <span>{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Product Gallery */}
        <div className="lg:col-span-7 space-y-4">
          <ProductGallery
            images={product.images}
            alt={product.name}
            imageFit="contain"
          />
        </div>

        {/* Product Information */}
        <div className="lg:col-span-5 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">
              {product.category}
            </p>

            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
              {product.name}
            </h1>

            <p className="text-lg text-welljio-muted mt-3">
              {product.tagline}
            </p>
          </div>

          <div
            className="flex items-center gap-2"
            data-testid="product-rating-summary"
          >
            <div
              className="flex items-center gap-0.5"
              aria-label={`Rated ${product.rating.toFixed(1)} out of 5`}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-4 h-4 ${
                    n <= Math.round(product.rating)
                      ? "fill-welljio-gold text-welljio-gold"
                      : "text-slate-300"
                  }`}
                  strokeWidth={1}
                />
              ))}
            </div>

            <span className="text-sm text-welljio-ink font-semibold ml-1">
              {product.rating.toFixed(1)}
            </span>

            <span className="text-sm text-welljio-muted">
              · {product.review_count.toLocaleString()} reviews
            </span>
          </div>

          <div className="flex items-end gap-3">
            <span className="font-heading text-4xl font-bold">
              {inr(product.price)}
            </span>

            {product.compare_at_price && (
              <>
                <span className="text-lg text-welljio-subtle line-through">
                  {inr(product.compare_at_price)}
                </span>

                <span className="text-sm font-medium text-welljio-green">
                  Save{" "}
                  {inr(product.compare_at_price - product.price)}
                </span>
              </>
            )}
          </div>

          <p className="text-welljio-muted leading-relaxed">
            {product.long_description}
          </p>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6 pt-2"
            data-testid="product-benefits-grid"
          >
            {product.benefits.slice(0, 4).map((b) => {
              const Icon = iconForBenefit(b);

              return (
                <div key={b} className="flex items-center gap-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-welljio-highlight/70 ring-1 ring-welljio-highlight flex items-center justify-center shrink-0 shadow-[inset_0_-2px_8px_rgba(56,189,248,0.10)]">
                    <Icon
                      className="w-7 h-7 sm:w-8 sm:h-8 text-welljio-accent"
                      strokeWidth={1.5}
                    />
                  </div>

                  <p className="text-sm sm:text-base text-welljio-ink font-medium leading-snug">
                    {b}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <div className="flex items-center bg-welljio-tint rounded-full">
              <button
                data-testid="qty-decrement"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-11 h-11 rounded-full hover:bg-white flex items-center justify-center"
              >
                <Minus
                  className="w-4 h-4"
                  strokeWidth={1.5}
                />
              </button>

              <span
                data-testid="qty-display"
                className="w-8 text-center"
              >
                {qty}
              </span>

              <button
                data-testid="qty-increment"
                onClick={() => setQty(qty + 1)}
                className="w-11 h-11 rounded-full hover:bg-white flex items-center justify-center"
              >
                <Plus
                  className="w-4 h-4"
                  strokeWidth={1.5}
                />
              </button>
            </div>

            <button
              data-testid="product-add-to-cart-button"
              onClick={async () => {
                await addItem(product.id, qty);
              }}
              className="flex-1 bg-welljio-ink text-white rounded-full py-4 font-medium hover:bg-welljio-accent transition-colors"
            >
              Add to Cart
            </button>

            <button
              type="button"
              data-testid="product-wishlist-button"
              onClick={() => wishToggle(product.id, product.name)}
              className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all ${
                wishHas(product.id)
                  ? "bg-welljio-coral border-welljio-coral text-white"
                  : "bg-white border-slate-200 text-welljio-ink hover:border-welljio-coral hover:text-welljio-coral"
              }`}
              aria-label={
                wishHas(product.id)
                  ? `Remove ${product.name} from wishlist`
                  : `Save ${product.name} to wishlist`
              }
              aria-pressed={wishHas(product.id)}
            >
              <Heart
                className={`w-5 h-5 ${
                  wishHas(product.id) ? "fill-current" : ""
                }`}
                strokeWidth={1.6}
              />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 pt-4 border-t border-slate-100">
            <div className="text-center">
              <Truck
                className="w-5 h-5 mx-auto mb-1 text-welljio-accent"
                strokeWidth={1.5}
              />
              <p className="text-xs text-welljio-muted">
                Free shipping
              </p>
            </div>

            <div className="text-center">
              <RotateCcw
                className="w-5 h-5 mx-auto mb-1 text-welljio-accent"
                strokeWidth={1.5}
              />
              <p className="text-xs text-welljio-muted">
                30-day returns
              </p>
            </div>

            <div className="text-center">
              <ShieldCheck
                className="w-5 h-5 mx-auto mb-1 text-welljio-accent"
                strokeWidth={1.5}
              />
              <p className="text-xs text-welljio-muted">
                1-year warranty
              </p>
            </div>
          </div>

          {/* Share buttons */}
          <div
            className="pt-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3"
            data-testid="product-share-section"
          >
            <span className="text-[10px] uppercase tracking-[0.28em] text-welljio-subtle font-semibold flex items-center gap-2">
              <Share2
                className="w-3 h-3"
                strokeWidth={1.8}
              />
              Share this product
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="share-twitter"
                onClick={() => share("twitter")}
                aria-label="Share on X / Twitter"
                className="w-11 h-11 rounded-full bg-welljio-tint hover:bg-welljio-accent hover:text-white text-welljio-ink flex items-center justify-center transition-colors"
              >
                <Twitter
                  className="w-4 h-4"
                  strokeWidth={1.6}
                />
              </button>

              <button
                type="button"
                data-testid="share-facebook"
                onClick={() => share("facebook")}
                aria-label="Share on Facebook"
                className="w-11 h-11 rounded-full bg-welljio-tint hover:bg-welljio-accent hover:text-white text-welljio-ink flex items-center justify-center transition-colors"
              >
                <Facebook
                  className="w-4 h-4"
                  strokeWidth={1.6}
                />
              </button>

              <button
                type="button"
                data-testid="share-whatsapp"
                onClick={() => share("whatsapp")}
                aria-label="Share on WhatsApp"
                className="w-11 h-11 rounded-full bg-welljio-tint hover:bg-welljio-green hover:text-white text-welljio-ink flex items-center justify-center transition-colors"
              >
                <MessageCircle
                  className="w-4 h-4"
                  strokeWidth={1.6}
                />
              </button>

              <button
                type="button"
                data-testid="share-copy"
                onClick={() => share("copy")}
                aria-label="Copy product link"
                className="w-11 h-11 rounded-full bg-welljio-tint hover:bg-welljio-coral hover:text-white text-welljio-ink flex items-center justify-center transition-colors"
              >
                <Link2
                  className="w-4 h-4"
                  strokeWidth={1.6}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <section
        className="mt-16 grid lg:grid-cols-2 gap-12"
        data-testid="product-details-section"
      >
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-3">
            Who should use this
          </p>

          <h2 className="font-heading text-3xl font-bold mb-6">
            Designed for your routine.
          </h2>

          <ul className="space-y-3">
            {product.who_should_use.map((w) => (
              <li
                key={w}
                className="flex items-start gap-3 text-welljio-ink"
              >
                <span className="w-6 h-6 rounded-full bg-welljio-highlight text-welljio-accent flex items-center justify-center mt-0.5">
                  <Check
                    className="w-3 h-3"
                    strokeWidth={2}
                  />
                </span>

                {w}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-3">
            Usage
          </p>

          <h2 className="font-heading text-3xl font-bold mb-6">
            How to use it.
          </h2>

          <ol className="space-y-3 list-none">
            {product.usage_instructions.map((u, i) => (
              <li
                key={i}
                className="flex items-start gap-3"
              >
                <span className="w-7 h-7 rounded-full bg-welljio-ink text-white text-xs flex items-center justify-center font-medium shrink-0">
                  {i + 1}
                </span>

                <span className="text-welljio-ink">
                  {u}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* AI Recommendation */}
      <section
        className="mt-14 rounded-[2rem] bg-gradient-to-br from-welljio-highlight to-white border border-slate-100 p-10 lg:p-14"
        data-testid="ai-suggestion-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-welljio-ink text-white flex items-center justify-center">
            <Sparkles
              className="w-5 h-5"
              strokeWidth={1.5}
            />
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-welljio-accent mb-2">
              Smart insight
            </p>

            <h3 className="font-heading text-2xl font-bold mb-3">
              Why this product fits you
            </h3>

            <p className="text-welljio-muted leading-relaxed max-w-2xl">
              The {product.name} targets{" "}
              {product.pain_relief.slice(0, 3).join(", ")} — common
              concerns for{" "}
              {product.who_should_use[0]?.toLowerCase()}.
              Pair with daily breaks and posture awareness for
              compounded results.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="mt-16 max-w-3xl mx-auto"
        data-testid="product-faq-section"
      >
        <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-3 text-center">
          Common questions
        </p>

        <h2 className="font-heading text-4xl font-bold tracking-tight text-center mb-8">
          Frequently asked.
        </h2>

        <Accordion
          type="single"
          collapsible
          className="space-y-3"
        >
          {product.faq.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border border-slate-100 rounded-2xl bg-white px-5"
            >
              <AccordionTrigger
                data-testid={`product-faq-trigger-${i}`}
                className="text-left font-heading font-semibold"
              >
                {f.q}
              </AccordionTrigger>

              <AccordionContent className="text-welljio-muted">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Reviews */}
      <section
        className="mt-20"
        data-testid="product-reviews-section"
      >
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-14">
          {/* Overview */}
          <div
            className="welljio-premium-card p-7"
            data-testid="rating-overview"
          >
            <p className="text-xs uppercase tracking-widest text-welljio-accent font-semibold mb-3">
              Customer reviews
            </p>

            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-heading text-5xl font-light tracking-tight text-welljio-ink">
                {product.rating.toFixed(1)}
              </span>

              <span className="text-welljio-muted text-sm">
                out of 5
              </span>
            </div>

            <div className="flex items-center gap-0.5 mb-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-4 h-4 ${
                    n <= Math.round(product.rating)
                      ? "fill-welljio-gold text-welljio-gold"
                      : "text-slate-300"
                  }`}
                  strokeWidth={1}
                />
              ))}
            </div>

            <p className="text-xs text-welljio-subtle mb-5">
              Based on {product.review_count.toLocaleString()}+
              verified customers
            </p>

            {/* Breakdown bars */}
            <div className="space-y-1.5">
              {[5, 4, 3, 2, 1].map((n) => {
                const count = ratingBreakdown[n] || 0;
                const total = reviews.length || 1;
                const pct = Math.round((count / total) * 100);

                return (
                  <div
                    key={n}
                    className="flex items-center gap-2 text-xs"
                  >
                    <span className="w-3 text-welljio-muted tabular-nums">
                      {n}
                    </span>

                    <Star
                      className="w-3 h-3 fill-welljio-gold text-welljio-gold"
                      strokeWidth={1}
                    />

                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-welljio-gold rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                        }}
                      />
                    </div>

                    <span className="w-7 text-right text-welljio-subtle tabular-nums">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>

            <button
              data-testid="open-write-review-button"
              onClick={() => {
                if (!user) {
                  toast.message("Sign in to leave a review", {
                    description:
                      "We keep reviews verified to the user account.",
                  });
                  return;
                }

                setShowReviewForm((v) => !v);
              }}
              className="mt-6 w-full welljio-btn-primary justify-center"
            >
              {showReviewForm ? "Cancel" : "Write a review"}
            </button>

            {!user && (
              <p className="text-[11px] text-welljio-subtle text-center mt-3">
                <Link
                  to="/login"
                  className="text-welljio-accent hover:underline"
                >
                  Sign in
                </Link>{" "}
                to leave a verified review.
              </p>
            )}
          </div>

          {/* Reviews list + form */}
          <div className="space-y-5">
            {showReviewForm && user && (
              <div
                className="welljio-premium-card p-6 space-y-4"
                data-testid="write-review-form"
              >
                <h3 className="font-heading text-lg font-semibold text-welljio-ink">
                  Share your experience
                </h3>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-welljio-subtle font-semibold mb-2">
                    Your rating
                  </p>

                  <div
                    className="flex items-center gap-1"
                    role="radiogroup"
                    aria-label="Rating"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={reviewForm.rating === n}
                        data-testid={`rating-input-${n}`}
                        onClick={() =>
                          setReviewForm((f) => ({
                            ...f,
                            rating: n,
                          }))
                        }
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Star
                          className={`w-6 h-6 ${
                            n <= reviewForm.rating
                              ? "fill-welljio-gold text-welljio-gold"
                              : "text-slate-300"
                          }`}
                          strokeWidth={1}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <input
                  data-testid="review-title-input"
                  type="text"
                  maxLength={80}
                  placeholder="A short headline"
                  value={reviewForm.title}
                  onChange={(e) =>
                    setReviewForm((f) => ({
                      ...f,
                      title: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-welljio-accent focus:outline-none text-sm"
                />

                <textarea
                  data-testid="review-body-input"
                  maxLength={600}
                  rows={4}
                  placeholder="What did you notice? Comfort, posture, sleep, fit..."
                  value={reviewForm.body}
                  onChange={(e) =>
                    setReviewForm((f) => ({
                      ...f,
                      body: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-welljio-accent focus:outline-none text-sm resize-none"
                />

                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-welljio-subtle">
                    Posting as{" "}
                    <strong className="text-welljio-ink">
                      {user.name}
                    </strong>
                  </span>

                  <button
                    type="button"
                    data-testid="submit-review-button"
                    onClick={submitReview}
                    disabled={reviewSubmitting}
                    className="welljio-btn-coral disabled:opacity-50"
                  >
                    {reviewSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Posting…
                      </>
                    ) : (
                      "Post review"
                    )}
                  </button>
                </div>
              </div>
            )}

            {reviews.length === 0 ? (
              <div
                className="welljio-premium-card p-8 text-center"
                data-testid="no-reviews"
              >
                <p className="text-welljio-muted text-sm">
                  No reviews yet. Be the first to share your
                  experience.
                </p>
              </div>
            ) : (
              <div
                className="grid sm:grid-cols-2 gap-5"
                data-testid="reviews-list"
              >
                {reviews.map((r) => (
                  <div
                    key={r.id}
                    className="welljio-premium-card p-6"
                  >
                    <div className="flex items-center gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3.5 h-3.5 ${
                            n <= r.rating
                              ? "fill-welljio-gold text-welljio-gold"
                              : "text-slate-300"
                          }`}
                          strokeWidth={1}
                        />
                      ))}
                    </div>

                    <h4 className="font-heading font-semibold text-welljio-ink mb-1.5">
                      {r.title}
                    </h4>

                    <p className="text-sm text-welljio-muted mb-3 leading-relaxed">
                      &ldquo;{r.body}&rdquo;
                    </p>

                    <div className="flex items-center justify-between text-xs text-welljio-subtle pt-2 border-t border-slate-100">
                      <span>— {r.name}</span>

                      {r.date && (
                        <span>
                          {new Date(r.date).toLocaleDateString(
                            undefined,
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            }
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related */}
      <section
        className="mt-16"
        data-testid="related-products-section"
      >
        <h2 className="font-heading text-4xl font-bold tracking-tight mb-10">
          You may also love
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {related.map((p, i) => (
            <ProductCard
              product={p}
              key={p.id}
              index={i}
            />
          ))}
        </div>
      </section>
    </div>
  );
}